import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Shield, RefreshCw, Save, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ROLES = ["admin", "nurse", "doctor", "pharmacy", "billing", "ip", "manager", "superadmin"];
const BRANCHES = ["PBY", "CHP", "VZK", "BCS", "BPC", "BNE"];
const FEATURES = [
  { key: "patient_register", label: "Register patients" },
  { key: "patient_bulk_import", label: "Bulk import patients" },
  { key: "vitals_entry", label: "Record vitals" },
  { key: "consultation", label: "Doctor consultation + Rx" },
  { key: "pharmacy_billing", label: "Pharmacy billing" },
  { key: "direct_bill", label: "Direct (walk-in) billing" },
  { key: "inventory_edit", label: "Edit inventory" },
  { key: "ip_admission", label: "IP admission + charting" },
  { key: "insurance_validate", label: "Validate insurance claims" },
  { key: "reports_eod", label: "End-of-day reports" },
  { key: "discharge_edit", label: "Edit discharge summary" },
  { key: "accounts_review", label: "Accounts · Tally review + EOD close" },
  { key: "marketing_followups", label: "Marketing · Follow-up book" },
  { key: "admin_vault", label: "Superadmin · Cross-branch vault" },
  { key: "admin_doctors", label: "Superadmin · Doctor mapping" },
  { key: "admin_users", label: "Superadmin · User management" },
  { key: "einvoice_edit", label: "Add / update IRN on bills" },
  { key: "tally_toggle", label: "Toggle bills into Tally ledger" },
  { key: "tally_xml_export", label: "Download Tally XML voucher pack" },
  { key: "excel_export", label: "Download Excel reports" },
  { key: "queue_public", label: "Open public queue board" },
  { key: "doctor_avatar", label: "Upload doctor headshots" },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.username}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.username}`);
      load();
    } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
  };
  const resetPwd = async (u) => {
    const pwd = window.prompt(`Reset password for ${u.username}. Enter new password (min 6 chars):`);
    if (!pwd || pwd.length < 6) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { password: pwd });
      toast.success(`Password reset for ${u.username}`);
    } catch (err) { toast.error(formatApiError(err, "Reset failed")); }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="user-mgmt-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">Superadmin · User management</div>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1 flex items-center gap-2">
            <Shield className="h-7 w-7" strokeWidth={1.5} /> Staff accounts
          </h1>
          <p className="text-[#5C5652]">Add/remove users, reset passwords, toggle features per staff. Actions are audit-logged server-side.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="um-refresh" className="h-10 px-3 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Refresh</button>
          <button onClick={() => setShowAdd(true)} data-testid="um-add" className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2"><UserPlus className="h-4 w-4" /> Add user</button>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-x-auto">
        {loading ? <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
          : <table className="w-full text-sm" data-testid="um-users-table">
              <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Username</th>
                  <th className="text-left px-4 py-3 font-bold">Name</th>
                  <th className="text-left px-4 py-3 font-bold">Role</th>
                  <th className="text-left px-4 py-3 font-bold">Branches</th>
                  <th className="text-left px-4 py-3 font-bold">Custom feature flags</th>
                  <th className="text-right px-4 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E3D9]">
                {users.map((u) => {
                  const flagsCount = Object.keys(u.feature_flags || {}).length;
                  return (
                  <tr key={u.id} data-testid={`um-row-${u.username}`}>
                    <td className="px-4 py-3 font-mono text-xs">{u.username}</td>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3"><span className="text-[10px] px-2 py-0.5 rounded bg-[#1A4331]/10 text-forest uppercase tracking-wider font-bold">{u.role}</span></td>
                    <td className="px-4 py-3 text-xs">{(u.branches || []).join(", ") || <span className="text-[#5C5652]">—</span>}</td>
                    <td className="px-4 py-3 text-xs">
                      {flagsCount === 0 ? <span className="text-[#5C5652]">Role default</span> : <span className="text-forest font-semibold">{flagsCount} overrides</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing(u)} data-testid={`um-edit-${u.username}`} title="Edit features & role"
                          className="h-8 px-2 rounded border border-sand hover:bg-[#F9F6F0] text-xs">Edit</button>
                        <button onClick={() => resetPwd(u)} data-testid={`um-reset-${u.username}`} title="Reset password"
                          className="h-8 w-8 rounded border border-sand hover:bg-[#F9F6F0] flex items-center justify-center text-[#5C5652]"><KeyRound className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(u)} data-testid={`um-del-${u.username}`} title="Delete"
                          className="h-8 w-8 rounded border border-sand hover:bg-[#C25946]/10 text-[#C25946] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>);
                })}
              </tbody>
            </table>}
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AddUserModal({ onClose, onSaved }) {
  const [f, setF] = useState({ username: "", password: "", name: "", role: "admin", branches: [] });
  const [busy, setBusy] = useState(false);
  const toggleBranch = (b) => setF((p) => ({ ...p, branches: p.branches.includes(b) ? p.branches.filter((x) => x !== b) : [...p.branches, b] }));
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin/users", f); toast.success(`Created ${f.username}`); onSaved(); }
    catch (err) { toast.error(formatApiError(err, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="um-add-modal">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-2xl text-forest">Add user</div>
          <button onClick={onClose} className="h-8 w-8 rounded border border-sand"><X className="h-4 w-4 mx-auto" /></button></div>
        <div className="space-y-3">
          <input data-testid="um-add-username" placeholder="Username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="w-full h-10 px-3 rounded border border-sand" />
          <input data-testid="um-add-password" type="password" placeholder="Password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full h-10 px-3 rounded border border-sand" />
          <input data-testid="um-add-name" placeholder="Display name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full h-10 px-3 rounded border border-sand" />
          <select data-testid="um-add-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full h-10 px-3 rounded border border-sand">{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
          <div>
            <div className="text-[10px] uppercase text-[#5C5652] mb-1">Branches</div>
            <div className="flex flex-wrap gap-1">
              {BRANCHES.map((b) => (
                <button key={b} type="button" onClick={() => toggleBranch(b)} data-testid={`um-add-br-${b}`}
                  className={`px-2 py-1 rounded text-xs font-bold ${f.branches.includes(b) ? "bg-forest text-white" : "bg-[#F9F6F0] text-[#5C5652] border border-sand"}`}>{b}</button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="um-add-submit" className="mt-5 w-full h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">{busy ? "Creating…" : "Create user"}</button>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [flags, setFlags] = useState(user.feature_flags || {});
  const [branches, setBranches] = useState(user.branches || []);
  const [role, setRole] = useState(user.role);
  const [busy, setBusy] = useState(false);
  const toggleFlag = (k) => setFlags((p) => ({ ...p, [k]: !p[k] }));
  const toggleBranch = (b) => setBranches((p) => p.includes(b) ? p.filter((x) => x !== b) : [...p, b]);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/users/${user.id}`, { role, branches, feature_flags: flags }); toast.success("Saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="um-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <div><div className="font-display text-2xl text-forest">{user.name}</div><div className="text-xs text-[#5C5652] font-mono">{user.username}</div></div>
          <button onClick={onClose} className="h-8 w-8 rounded border border-sand"><X className="h-4 w-4 mx-auto" /></button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase text-[#5C5652] mb-1">Role</div>
            <select data-testid="um-edit-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-10 px-3 rounded border border-sand">{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
          </div>
          <div>
            <div className="text-[10px] uppercase text-[#5C5652] mb-1">Branches</div>
            <div className="flex flex-wrap gap-1">
              {BRANCHES.map((b) => (<button key={b} type="button" onClick={() => toggleBranch(b)} data-testid={`um-edit-br-${b}`}
                className={`px-2 py-1 rounded text-xs font-bold ${branches.includes(b) ? "bg-forest text-white" : "bg-[#F9F6F0] text-[#5C5652] border border-sand"}`}>{b}</button>))}
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2 ornament-dot">Feature access</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {FEATURES.map((ft) => {
              const on = flags[ft.key] !== false; // undefined = role default (treated on)
              return (
                <label key={ft.key} className={`flex items-center justify-between gap-2 p-3 rounded border ${flags[ft.key] === false ? "bg-[#C25946]/[0.06] border-[#C25946]/30" : "bg-white border-sand"}`}>
                  <span className="text-sm">{ft.label}</span>
                  <input type="checkbox" data-testid={`um-flag-${ft.key}`} checked={on} onChange={() => setFlags({ ...flags, [ft.key]: !on })} className="h-4 w-4 accent-[#1A4331]" />
                </label>);
            })}
          </div>
          <div className="text-[11px] text-[#5C5652] mt-2">Unchecked = feature turned OFF for this staff. Empty state = role default. (Client enforcement pending — server routes still gate by role.)</div>
        </div>
        <button onClick={save} disabled={busy} data-testid="um-edit-save" className="mt-5 w-full h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
